import { WalletConfig, WalletField } from "@/types/DocumentTemplate";
import {
  BarcodeBlock,
  FieldRow,
  PassCard,
  PassHeader,
  ThumbnailImage,
  passInsetX,
  useColors,
  emptyValueMark,
  HIDE_ROW,
  useDeviceOnly,
  useRowCertainty,
  useTemplateRenderer,
  useVisibleCounts,
  alignOf,
  FitText,
} from "./sharedPreviewBits";

type Props = {
  config: WalletConfig;
  currentLanguage: string;
};

export function GenericPreview({ config, currentLanguage }: Props) {
  const colors = useColors(config);
  const deviceOnly = useDeviceOnly();
  const certaintyOfPrimary = useRowCertainty("primaryFields");
  const certaintyOfSecondary = useRowCertainty("secondaryFields");
  const certaintyOfAuxiliary = useRowCertainty("auxiliaryFields");
  
  // The primary value is drawn here rather than through `FieldRow`, so the
  // "resolved to nothing" rule has to be asked for explicitly — this used to
  // be another hand-copy of `deviceOnly ? null : "—"`. `colors.fg`, not
  // `colors.lbl`: this lane has always drawn its dash in the value colour.
  const primaryMark = (field: WalletField | undefined): React.ReactNode => {
    const mark = emptyValueMark({
      deviceOnly,
      certainty: field ? certaintyOfPrimary(field) : undefined,
      labelColor: colors.fg,
    });
    return mark === HIDE_ROW ? null : (mark as React.ReactNode);
  };
  /**
   * Is this slot's row one the delivered pass will NOT carry?
   *
   * The label of a hand-rolled primary slot renders outside `primaryMark`, so
   * suppressing only the VALUE left "PLATZ" standing over nothing — the exact
   * picture this whole change exists to remove, surviving on three pass types.
   * An earlier comment here called that "the closest it can get"; it was not,
   * the label is a `&&` away.
   */
  const primaryGone = (field: WalletField | undefined) =>
    deviceOnly &&
    !!field?.key &&
    certaintyOfPrimary(field) === "absent";
  
  
  const { render, text } = useTemplateRenderer();
  const visible = useVisibleCounts(config);
  const primary = config.primaryFields?.[0];

  return (
    <PassCard bg={colors.bg}>
      <PassHeader
        config={config}
        currentLanguage={currentLanguage}
        colors={colors}
      />

      {/* Primary + Thumbnail side by side */}
      <div className="py-3 flex items-start gap-3" style={passInsetX}>
        {/* The same `textAlignment` the pass carries. This lane draws its
            primary field by hand instead of through `FieldRow`, and was
            missed when the key was first honoured — so the identical field
            aligned on some pass types and not on others. */}
        <div
          className="flex-1 min-w-0"
          style={(() => {
            const a = alignOf(primary?.textAlignment);
            return a ? { textAlign: a } : undefined;
          })()}
        >
          {!primaryGone(primary) && (
            <div
              className="text-[10px] uppercase tracking-wider truncate"
              style={{ color: colors.lbl }}
            >
              {text(primary?.label, currentLanguage)
                ? render(text(primary?.label, currentLanguage))
                : " "}
            </div>
          )}
          {/* Hand-rolled primary — it bypasses `FieldRow`, so `FitText` has to
              be named here or this lane keeps the old clip-only behaviour while
              every other value on the card shrinks. These four hand-rolled
              lanes have drifted from the shared one before, which is why
              `alignmentAppliesCallSites` exists. */}
          <FitText
            // No `align`: the wrapper above carries this field's
            // `textAlign`, and `FitText` inherits it.
            className="text-lg font-semibold whitespace-nowrap overflow-hidden"
            style={{ color: colors.fg }}
          >
            {text(primary?.value, currentLanguage)
              ? render(text(primary?.value, currentLanguage))
              : primaryMark(primary)}
          </FitText>
        </div>
        <ThumbnailImage url={config.thumbnailImageUrl} size={80} />
      </div>

      {config.secondaryFields && config.secondaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.secondaryFields}
            certaintyOf={certaintyOfSecondary}
            currentLanguage={currentLanguage}
            colors={colors}
          visibleCount={visible.secondary}
          />
        </div>
      )}

      {config.auxiliaryFields && config.auxiliaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.auxiliaryFields}
            certaintyOf={certaintyOfAuxiliary}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.auxiliary}
          />
        </div>
      )}

      <BarcodeBlock config={config} colors={colors} />
      {/* `description` is metadata (add-sheet title + VoiceOver), never on face. */}
    </PassCard>
  );
}
